import { BrowserExtension } from '@/behaviour/browser_extension/index'
import { $ } from '@/libs/jquery/index'
import { noUiSlider } from '@/libs/nouislider/index'
import { routes } from '@/libs/routes/index'
import { ILeadsResponse } from '@/typings/leads_response'
import { FormatStrings} from '@/utilities/format_strings'
import { Messages } from '@/utilities/messages'
import { PopUps } from '@/utilities/popups'
import { IModel, IParam, IResponse } from '@/views/application/leads/potok/company_loan/interfaces'
import { attachDatataFieldsToLead } from '@/views/application/leads/shared/dadata/index'

export class LeadsPotokCompanyLoan implements IClass {
  // Page: /potok/company_loan/leads/

  public base: JQuery = null

  public readonly selectors: ISelectors = {
    base: '[data-selector="leadsPotokCompanyLoan"]',
    btnLoanCount: '[data-selector="btnLoanCount"]',
    feeAmountInput: '[data-selector="input_fee_amount"]',
    feeAmountLoan: '[data-selector="feeAmountLoan"]',
    feePercentInput: '[data-selector="input_fee_percent"]',
    feePercentLoan: '[data-selector="feePercentLoan"]',
    form: '[data-selector="companyLoanLead"]',
    formWrapper: '[data-selector="companyLoanForm"]',
    leadAmountInput: '[data-selector="leadAmountInput"]',
    leadPeriodInput: '[data-selector="leadPeriodInput"]',
    paymentAmountInput: '[data-selector="input_payment_amount"]',
    paymentAmountLoan: '[data-selector="paymentAmountLoan"]',
    rangeLoanDays: '[data-selector="rangeLoanDays"]',
    rangeLoanSum: '[data-selector="rangeLoanSum"]',
    ratePercentInput: '[data-selector="input_rate_percent"]',
    ratePercentLoan: '[data-selector="ratePercentLoan"]',
    results: '[data-selector="companyLoanResults"]',
    totalLoanSum: '[data-selector="totalLoanSum"]',
    totalLoanTerm: '[data-selector="totalLoanTerm"]',
  }

  private model: IModel = {
    sum: 1000000,
    term: 6,
  }

  private coefficient: number = 1000000
  private minimum: number = 500000

  // слайдер-бегунок по сумме кредита
  private rangeLoanSum: HTMLElement = null
  // слайдер-бегунок по сроку кредита
  private rangeLoanDays: HTMLElement = null

  constructor() {
    this.base = $(this.selectors.base)

    if (this.base.length) {
      this.init()
    }
  }

  public init(): void {
    this.bindEvents()
    BrowserExtension.setCookies('company_loan')
    this.startCustomRangeSlider()
    this.updateCustomRangeInput()
  }

  private bindEvents(): void {
    $(document)
      .on('form:lead:rendered', () => attachDatataFieldsToLead(this.selectors.base))
      .on('ajax:success', this.selectors.form, (e, response, xhr) => {
        this.handleSuccessLeadForm(e, response, xhr)
      })
    this.base
      .on('change', this.selectors.totalLoanTerm, (e) => this.updateSliderTerms(e))
      .on('change', this.selectors.totalLoanSum, (e) => this.updateSliderSum(e))
      .on('click', this.selectors.btnLoanCount, (e) => this.sendData(e))
  }

  private handleSuccessLeadForm(e: JQuery.Event, response: ILeadsResponse, xhr: string): void {
    const orderNumber: string = response.number
    PopUps.showPopupByContent({
      autoClose: 5000,
      closeCallback: () => window.location.reload(true),
      openCallback: () => {
        this.ref(this.selectors.results).hide()
        this.ref(this.selectors.formWrapper).hide()
      },
      textContent: Messages.requestLeadSuccess(orderNumber),
    })
  }

  // отправка запроса на вычисления
  private sendData(e: JQuery.ClickEvent): void {
    const param: IParam = {
      amount: this.model.sum,
      period: this.model.term,
    }

    const renderUrl: string = routes.calculate_leads_path('potok', 'company_loan', param)
    $.ajax({
      success: (response) => {
        this.renderData(response)
      },
      url: renderUrl,
    })
  }

  // отображение данных в калькуляторе
  private renderData(response: IResponse): void {
    this.ref(this.selectors.ratePercentLoan).html(String(response.rate_percent))
    this.ref(this.selectors.ratePercentInput).val(response.rate_percent)

    const fee: number = Math.round(response.fee_amount * 100) / 100
    const feeAmount: string = FormatStrings.formatDecimalToHuman(String(fee))
    this.ref(this.selectors.feeAmountLoan).text(feeAmount)
    this.ref(this.selectors.feeAmountInput).val(fee)

    this.ref(this.selectors.feePercentLoan).html(String(response.fee_percent))
    this.ref(this.selectors.feePercentInput).val(response.fee_percent)

    const pay: number = Math.round(response.payment_amount * 100) / 100
    const paymentAmount: string = FormatStrings.formatDecimalToHuman(String(pay))
    this.ref(this.selectors.paymentAmountLoan).text(paymentAmount)
    this.ref(this.selectors.paymentAmountInput).val(pay)

    const amount: string = FormatStrings.formatDecimalToHuman(String(this.model.sum))
    this.ref(this.selectors.leadAmountInput).val(amount).valid()
    this.ref(this.selectors.leadPeriodInput).val(this.model.term).valid()

    this.ref(this.selectors.results).slideDown()
    this.ref(this.selectors.btnLoanCount).removeClass('button--orange').addClass('button--blue')
  }

  private updateModel(field: string, value: number): void {
    this.model[field] = value
    this.updateUI(field)
  }

  private updateUI(field: string): void {
    const result: number = this.model[field]
    this.ref(`[data-selector="${field}Data"]`).html(String(result))
  }

  // инициализация слайдеров
  private startCustomRangeSlider(): void {
    this.rangeLoanSum = this.ref(this.selectors.rangeLoanSum)[0]
    noUiSlider.create(this.rangeLoanSum, {
      pips: {
        format: {
          to: (value: number) => {
            if (value >= 1000000) {
              return value / 1000000 + ' м.'
            } else {
              return value / 1000 + ' т.'
            }
          },
        },
        mode: 'values',
        values: [500000, 1000000, 3000000, 5000000, 10000000, 20000000, 30000000],
      },
      range: {
        '23%': [1000000, 50000],
        '50%': [5000000, 1000000],
        '67%': [10000000, 1000000],
        'max': [30000000],
        'min': [500000],
      },
      start: this.model.sum,
      step: 50000,
    })
    $(this.rangeLoanSum).find('div.noUi-marker').remove()

    this.rangeLoanDays = this.ref(this.selectors.rangeLoanDays)[0]
    noUiSlider.create(this.rangeLoanDays, {
      pips: {
        format: {
          to: (value: number) => {
            return value + ' м.'
          },
        },
        mode: 'values',
        values: [1, 2, 3, 6, 8, 12, 18],
      },
      range: {
        '37%': [3],
        '51%': [6],
        '67%': [8],
        '83%': [12],
        'max': [18],
        'min': [1],
      },
      start: this.model.term,
      step: 1,
    })
  }

  // биндинг события на слайдер для обновления значения в слайдере и инпуте
  private updateCustomRangeInput(): void {
    this.rangeLoanSum.noUiSlider.on('update', (values, handle) => {
      this.updateInputValue(values, handle, 'Sum')
    })
    this.rangeLoanDays.noUiSlider.on('update', (values, handle) => {
      this.updateInputValue(values, handle, 'Term')
    })
  }

  // обновить значение в слайдере для срока кредита
  // минимум 1, максимум 18
  // присваиваем слайдеру, потом идем к инпуту
  private updateSliderTerms(event: JQuery.ChangeEvent): void {
    let term: number = event.target.value
    term = (term < 1) ? 1
      : (term > 18)
      ? 18 : term
    this.rangeLoanDays.noUiSlider.set([term])
    $(this.rangeLoanDays).val(term)
    this.updateInputValue([String(term)], 0, 'Term')
    this.updateModel('term', term)
  }

  // обновить значение в слайдере для суммы кредита
  // минимум 500000, максимум 30000000
  // присваиваем слайдеру, потом идем к инпуту
  private updateSliderSum(event: JQuery.ChangeEvent): void {
    let sum: number = parseInt(event.target.value.replace(/\s/gi, ''), 10)
    sum = (sum < 500000) ? 500000
      : (sum > 30000000)
      ? 30000000 : sum
    this.rangeLoanSum.noUiSlider.set([sum])
    $(this.rangeLoanSum).val(sum)
    this.updateInputValue([String(sum)], 0, 'Sum')
    this.updateModel('sum', sum)
  }

  // обновить значение в инпуте, связанном со слайдером
  private updateInputValue(values: string[], handle: number, field: string): void {
    const amount: number = parseInt(values[handle], 10)
    this.ref(`[data-selector='countLoan${field}']`).val(amount)
    const formatedAmount: string = FormatStrings.formatNumbersToHuman(String(amount))
    this.ref(`[data-selector='totalLoan${field}']`).val(formatedAmount)
    this.updateModel(field.toLowerCase(), amount)
  }

  private ref(selector: string): JQuery {
    return this.base.find(selector)
  }
}
